import React, { useState, useEffect, useContext } from "react";
import { Home, BookOpen, FileText, Calendar, Settings, User, Hand, X } from 'lucide-react';
import { NavLink } from "react-router-dom";
import { ApiUrlContext } from "../App";

const menu = [
  { label: "Anasayfa", path: "/panel", icon: <Home /> },
  { label: "Kurslar", path: "/courses", icon: <BookOpen /> },
  { label: "Ödevler", path: "/assessments", icon: <FileText /> },
  { label: "Randevu Sistemi", path: "/appointments", icon: <Calendar /> },
  { label: "Admin Panel", path: "/admin", icon: <Settings />, adminOnly: true },
  { label: "Öğretmen Panel", path: "/instructor", icon: <User />, instructorOnly: true },
  { label: "Bize Katıl", path: "/contact", icon: <Hand /> },
];

const styles = `
.sidebar {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: #ffffff !important;
  border-right: 1px solid #edf2f7;
}

.sidebar-nav {
  display: flex !important;
  flex-direction: column !important;
  gap: 4px !important;
  padding: 0 16px !important;
  width: 100% !important;
}

.sidebar-nav .nav-link {
  display: flex !important;
  align-items: center !important;
  padding: 14px 20px !important;
  text-decoration: none !important;
  color: #4a5568 !important; 
  border-radius: 12px !important;
  transition: all 0.2s ease !important;
  font-size: 15px !important;
  font-weight: 500 !important;
}

.sidebar-nav .nav-link:hover {
  background-color: rgba(102, 126, 234, 0.06) !important;
  color: #2d3748 !important;
}

.sidebar-nav .nav-link.active {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.08) 100%) !important;
  color: #4c51bf !important;
  font-weight: 600 !important;
}

.sidebar-nav .nav-icon {
  margin-right: 14px !important;
  width: 20px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  color: inherit !important;
}

.sidebar-logo {
  font-weight: 700;
  font-size: 22px;
  color: #2d3748;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
}

.sidebar-close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  color: #4a5568;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1011;
}

.sidebar-footer {
  margin-top: auto;
  padding: 20px 24px;
  border-top: 1px solid #e2e8f0;
  font-size: 12px;
  color: #a0aec0;
  text-align: center;
}

@media (max-width: 768px) {
  .sidebar {
    width: 280px !important;
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    height: 100vh !important;
    z-index: 10000 !important; 
    box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15) !important;
    display: flex !important;
    flex-direction: column !important;
  }
}
`;

if (!document.getElementById('sidebar-styles')) {
  const styleSheet = document.createElement("style");
  styleSheet.id = 'sidebar-styles';
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

export default function Sidebar({ onClose, isMobile }) {
  // Eğer localStorage'da rol varsa başlangıç değeri yapalım, yoksa sen admin olduğun için varsayılanı admin tutalım ki kilitlenmesin
  const [userRole, setUserRole] = useState(localStorage.getItem("userRole") || "admin");
  const apiUrl = useContext(ApiUrlContext);

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const token = localStorage.getItem("token");
        if (token && apiUrl) {
          const response = await fetch(`${apiUrl}/user/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.ok) {
            const contentType = response.headers.get("content-type") || "";
            if (contentType.includes("application/json")) {
              const userData = await response.json();
              setUserRole(userData.role || "student");
              localStorage.setItem("userRole", userData.role || "student");
            }
          }
        }
      } catch (error) {
        console.error("Kullanıcı rol bilgisi alınamadı:", error);
      }
    };

    fetchUserRole();
  }, [apiUrl]);

  const filteredMenu = menu.filter(item => {
    if (item.adminOnly) return userRole === "admin";
    if (item.instructorOnly) return userRole === "instructor" || userRole === "admin";
    return true;
  });

  return (
    <>
      {isMobile && onClose && (
        <div
          className="sidebar-overlay"
          onClick={onClose}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(2px)",
            zIndex: 9999,
          }}
        />
      )}

      <aside
        style={{
          display: "flex",
          flexDirection: "column",
          padding: "28px 0",
          height: "100vh",
          overflowY: "auto",
          width: isMobile ? 280 : 250, 
          position: isMobile ? "fixed" : "relative"
        }}
        className="sidebar"
      >
        {onClose && (
          <button onClick={onClose} className="sidebar-close-btn" aria-label="Menüyü kapat">
            <X size={20} />
          </button>
        )}
        
        <div style={{ padding: "0 16px", marginBottom: "30px" }}>
          <a className="sidebar-logo" href="https://ihh.org.tr/" target="_blank" rel="noopener noreferrer">
            <Home size={24} style={{ color: "#667eea" }} />
            İyiliğin Mucitleri
          </a>
        </div>

        {/* Loading durumuna bakılmaksızın menü doğrudan render ediliyor */}
        <nav className="sidebar-nav" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {filteredMenu.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              onClick={isMobile ? onClose : undefined}
              end
            >
              <span className="nav-icon">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div style={{ opacity: 0.8, marginBottom: 4 }}>İHH Eğitim Platformu</div>
          <div style={{ fontSize: 11 }}>v2.1 • 2026</div>
        </div>
      </aside>
    </>
  );
}